import { useState, useEffect } from 'react';
import { api } from '../lib/api';
import MyRequests from '../components/MyRequests';

const EMPTY_FORM = { productDescription:'', brand:'', quantity:'', requiredByDate:'', additionalNotes:'' };

export default function ProductRequest() {
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  // When the form is opened via a "Request to order" click on a special-
  // order catalog item, OrderGuide.jsx writes a prefill blob into
  // sessionStorage. We pick it up here, populate the form, and surface a
  // small contextual banner so the user knows we recognized which item
  // they're requesting.
  const [specialOrderName, setSpecialOrderName] = useState('');

  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('productRequestPrefill');
      if (!raw) return;
      const data = JSON.parse(raw);
      setForm(f => ({
        ...f,
        productDescription: data.productDescription || f.productDescription,
        brand: data.brand || f.brand,
        additionalNotes: data.additionalNotes || f.additionalNotes,
      }));
      if (data.sourceLabel) setSpecialOrderName(data.sourceLabel);
      sessionStorage.removeItem('productRequestPrefill');
    } catch (_) { /* malformed prefill — ignore */ }
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.productDescription) return;
    setSubmitting(true);
    setError('');
    try {
      await api.requestProduct(form);
      setSubmitted(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  if (submitted) return (
    <div style={styles.successWrap}>
      <div style={styles.successIcon}>✓</div>
      <h2 style={styles.successTitle}>Request Sent!</h2>
      <p style={styles.successText}>Your request has been sent to the Silver Service team. We'll be in touch shortly.</p>
      <button style={styles.backBtn} onClick={() => { setSubmitted(false); setForm(EMPTY_FORM); setSpecialOrderName(''); }}>
        Submit Another Request
      </button>
    </div>
  );

  const isSpecialOrder = !!specialOrderName;

  return (
    <div style={styles.root}>
      <div style={styles.header}>
        <h1 style={styles.title}>{isSpecialOrder ? 'Request a Special Order' : "Can't Find a Product?"}</h1>
        <p style={styles.subtitle}>
          {isSpecialOrder
            ? "Special-order items aren't stocked for direct ordering. Send us the request below and we'll confirm pricing and availability."
            : "Submit a request and we'll track it down for you. We respond within 1 business day."}
        </p>
      </div>

      {isSpecialOrder && (
        <div style={styles.specialOrderBanner}>
          <span style={styles.specialOrderBannerLabel}>Requesting:</span>
          <strong>{specialOrderName}</strong>
        </div>
      )}

      <div style={styles.card}>
        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.field}>
            <label style={styles.label}>Product Description <span style={{color:'#e74c3c'}}>*</span></label>
            <input style={styles.input} placeholder="e.g. Illy Classico Whole Bean 1kg" value={form.productDescription} onChange={e => set('productDescription', e.target.value)} required />
          </div>
          <div style={styles.twoCol}>
            <div style={styles.field}>
              <label style={styles.label}>Brand (optional)</label>
              <input style={styles.input} placeholder="e.g. Illy, Oatly" value={form.brand} onChange={e => set('brand', e.target.value)} />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Quantity Needed</label>
              <input style={styles.input} placeholder="e.g. 2 cases" value={form.quantity} onChange={e => set('quantity', e.target.value)} />
            </div>
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Required By Date</label>
            <input style={styles.input} type="date" value={form.requiredByDate} onChange={e => set('requiredByDate', e.target.value)} />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Additional Notes</label>
            <textarea style={{...styles.input, resize:'vertical', minHeight:80}} placeholder="Any other details, context, or urgency..." value={form.additionalNotes} onChange={e => set('additionalNotes', e.target.value)} />
          </div>

          {error && <div style={styles.error}>{error}</div>}

          <button style={styles.btn} type="submit" disabled={submitting}>
            {submitting ? 'Sending...' : 'Send Request to Silver Service'}
          </button>
        </form>
      </div>
      <MyRequests type="product" />
    </div>
  );
}

const styles = {
  root: { maxWidth: 600, margin: '0 auto' },
  header: { marginBottom:'1.5rem' },
  title: { fontSize:'1.5rem', fontWeight:700, color:'#333', margin:0 },
  subtitle: { fontSize:'0.85rem', color:'rgba(0,0,0,0.45)', margin:'0.4rem 0 0' },
  card: { background:'#fff', border:'1px solid #e0e0e0', borderRadius:10, padding:'2rem', boxShadow:'0 1px 3px rgba(0,0,0,0.04)' },
  specialOrderBanner: { display:'flex', gap:'0.6rem', alignItems:'baseline', flexWrap:'wrap', background:'rgba(165,11,24,0.06)', border:'1px solid rgba(165,11,24,0.18)', borderRadius:8, padding:'0.7rem 1rem', marginBottom:'1rem', fontSize:'0.9rem', color:'#333' },
  specialOrderBannerLabel: { fontSize:'0.72rem', fontWeight:600, letterSpacing:'0.06em', textTransform:'uppercase', color:'#A50B18' },
  form: { display:'flex', flexDirection:'column', gap:'1.25rem' },
  twoCol: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'1rem' },
  field: { display:'flex', flexDirection:'column', gap:'0.4rem' },
  label: { fontSize:'0.78rem', color:'rgba(0,0,0,0.5)', fontWeight:500, letterSpacing:'0.04em' },
  input: { background:'#fff', border:'1px solid #ddd', borderRadius:6, padding:'0.7rem 0.9rem', color:'#333', fontSize:'0.9rem', outline:'none', fontFamily:'inherit' },
  error: { background:'rgba(231,76,60,0.08)', border:'1px solid rgba(231,76,60,0.25)', borderRadius:6, padding:'0.75rem 1rem', color:'#c0392b', fontSize:'0.85rem' },
  btn: { background:'#A50B18', color:'#fff', border:'none', borderRadius:6, padding:'0.85rem', fontSize:'0.95rem', fontWeight:700, cursor:'pointer' },
  successWrap: { maxWidth:400, margin:'4rem auto', textAlign:'center' },
  successIcon: { width:64, height:64, background:'rgba(39,174,96,0.1)', border:'2px solid #27ae60', borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 1.5rem', fontSize:'1.8rem', color:'#27ae60' },
  successTitle: { fontSize:'1.4rem', color:'#333', margin:'0 0 0.5rem' },
  successText: { color:'rgba(0,0,0,0.5)', lineHeight:1.6, margin:'0 0 2rem' },
  backBtn: { background:'none', border:'1px solid #ddd', color:'rgba(0,0,0,0.5)', borderRadius:6, padding:'0.7rem 1.5rem', cursor:'pointer', fontSize:'0.9rem' },
};
